import { Box, GlobalStyles } from '@mui/material';
import { motion } from 'framer-motion';
import eloFace from "../../../public/main/eloFace.png"

export default function EloOrbit({
    size = 420,
    sunSize = 220,
    sunImage = eloFace, // central image
    planets = [
        // { size: 38, distance: 80, duration: 12, color: '#FFB86B', label: 'Find' },
        // { size: 42, distance: 110, duration: 14, color: '#7BD389', label: 'Qualify' },
        { size: 34, distance: 140, duration: 16, color: '#6EC6FF', label: 'CRM' },
        { size: 38, distance: 170, duration: 18, color: '#FF6E6E', label: 'Outrch' },
        { size: 40, distance: 200, duration: 20, color: '#FFD66E', label: 'Nurture' },
        { size: 35, distance: 230, duration: 22, color: '#B28DFF', label: 'Book' },
        { size: 38, distance: 260, duration: 24, color: '#88F2A4', label: 'Follow' },
        { size: 38, distance: 290, duration: 26, color: '#FFA1D1', label: 'Track' },
        { size: 38, distance: 320, duration: 28, color: '#6FE7FF', label: 'Align' },
        { size: 38, distance: 350, duration: 30, color: '#FFAB91', label: 'Skills' },
        { size: 45, distance: 380, duration: 32, color: '#E5FF6F', label: 'Feedback' },
        { size: 38, distance: 410, duration: 34, color: '#FFDF6E', label: 'Trends' },
    ],
}) {
    const containerVariant = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: { opacity: 1, scale: 1, transition: { staggerChildren: 0.08 } },
    };

    const sunVariant = {
        hidden: { scale: 0 },
        visible: { scale: 1, transition: { type: 'spring', stiffness: 260, damping: 20 }, zIndex: 2 },
    };

    // Generate an array for stars
    const stars = Array.from({ length: 150 }).map(() => ({
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        size: Math.random() * 2 + 1,
        delay: `${Math.random() * 5}s`,
        duration: `${2 + Math.random() * 3}s`,
    }));

    return (
        <>
            <GlobalStyles
                styles={{
                    '@keyframes orbit-rotate': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                    },
                    '@keyframes planet-spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                    },
                    '@keyframes sun-pulse': {
                        '0%, 100%': {
                            boxShadow: '0 0 320px rgba(255, 184, 107, 0.5), 0 0 960px rgba(255, 184, 107, 0.3)'
                        },
                        '50%': {
                            boxShadow: '0 0 480px rgba(255, 184, 107, 0.8), 0 0 1440px rgba(255, 184, 107, 0.5)'
                        },
                    },
                    '@keyframes star-blink': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.2 }
                    },
                    '.starfield': {
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        top: 0,
                        left: 0,
                        overflow: 'hidden',
                        zIndex: 1,
                    },
                    '.star': {
                        position: 'absolute',
                        background: 'white',
                        borderRadius: '50%',
                        animation: 'star-blink 2s infinite ease-in-out',
                    }
                }}
            />

            <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariant}
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}
            >
                {/* Starfield background */}
                <div className="starfield">
                    {stars.map((s, i) => (
                        <div
                            key={i}
                            className="star"
                            style={{
                                left: s.left,
                                top: s.top,
                                width: s.size,
                                height: s.size,
                                animationDelay: s.delay,
                                animationDuration: s.duration,
                            }}
                        />
                    ))}
                </div>

                <Box
                    sx={{
                        width: size,
                        height: size,
                        position: 'relative',
                        display: 'grid',
                        placeItems: 'center',
                        userSelect: 'none',
                    }}
                >
                    {planets.map((p, i) => {
                        const orbitRadius = p.distance;
                        const orbitStyle = {
                            position: 'absolute',
                            width: `${orbitRadius * 2}px`,
                            height: `${orbitRadius * 2}px`,
                            left: `calc(50% - ${orbitRadius}px)`,
                            top: `calc(50% - ${orbitRadius}px)`,
                            borderRadius: '50%',
                            border: '1px solid rgba(255,255,255,0.2)',
                            boxShadow: '0 0 10px rgba(255,255,255,0.1)',
                            animation: `orbit-rotate ${p.duration}s linear infinite`,
                            animationDelay: `${i * -1.8}s`,
                            transformOrigin: '50% 50%',
                        };

                        const planetStyle = {
                            position: 'absolute',
                            left: '50%',
                            top: `${-(p.size) / 2}px`,
                            transform: 'translateX(-50%)',
                            width: `${p.size}px`,
                            height: `${p.size}px`,
                            borderRadius: '50%',
                            background: p.color,
                            boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 9,
                            color: '#000',
                            fontWeight: 700,
                            animation: `planet-spin 8s linear infinite`,
                        };

                        return (
                            <Box key={i} sx={orbitStyle}>
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.85 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.6, delay: 0.12 * i }}
                                    style={{ width: '100%', height: '100%', position: 'relative' }}
                                >
                                    <Box sx={planetStyle}>{p.label}</Box>
                                </motion.div>
                            </Box>
                        );
                    })}

                    {/* Pulsating Sun with Image */}
                    <motion.div variants={sunVariant}>
                        <Box
                            sx={{
                                width: sunSize,
                                height: sunSize,
                                borderRadius: '999px',
                                display: 'grid',
                                placeItems: 'center',
                                background: 'radial-gradient(circle at 30% 30%, #FFE680, #FFB86B 55%, #FF9A4D 100%)',
                                boxShadow:
                                    '0 12px 30px rgba(255,165,60,0.18), 0 6px 14px rgba(0,0,0,0.18), inset 0 -8px 20px rgba(0,0,0,0.06)',
                                border: '1px solid rgba(255,255,255,0.18)',
                                animation: 'sun-pulse 3s ease-in-out infinite',
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                component="img"
                                src={sunImage}
                                alt="Elo"
                                sx={{
                                    width: '80%',
                                    height: '80%',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                }}
                            />
                        </Box>
                    </motion.div>
                </Box>
            </motion.div>
        </>
    );
}







// import { Box, GlobalStyles } from '@mui/material';
// import { motion } from 'framer-motion';
// import eloFace from "../../../public/main/eloFace.png"

// export default function EloOrbit({
//     size = 420,
//     sunSize = 220,
//     sunImage = eloFace, // central image
//     planets = [
//         { size: 34, distance: 140, duration: 16, color: '#6EC6FF', label: 'CRM' },
//         { size: 38, distance: 170, duration: 18, color: '#FF6E6E', label: 'Outrch' },
//         { size: 40, distance: 200, duration: 20, color: '#FFD66E', label: 'Nurture' },
//         { size: 35, distance: 230, duration: 22, color: '#B28DFF', label: 'Book' },
//         { size: 38, distance: 260, duration: 24, color: '#88F2A4', label: 'Follow' },
//         { size: 38, distance: 290, duration: 26, color: '#FFA1D1', label: 'Track' },
//         { size: 38, distance: 320, duration: 28, color: '#6FE7FF', label: 'Align' },
//         { size: 38, distance: 350, duration: 30, color: '#FFAB91', label: 'Skills' },
//         { size: 45, distance: 380, duration: 32, color: '#E5FF6F', label: 'Feedback' },
//         { size: 38, distance: 410, duration: 34, color: '#FFDF6E', label: 'Trends' },
//     ],
// }) {
//     const containerVariant = {
//         hidden: { opacity: 0, scale: 0.9 },
//         visible: { opacity: 1, scale: 1, transition: { staggerChildren: 0.08 } },
//     };

//     const sunVariant = {
//         hidden: { scale: 0 },
//         visible: { scale: 1, transition: { type: 'spring', stiffness: 260, damping: 20 }, zIndex: 2 },
//     };

//     // Generate blinking stars
//     const stars = Array.from({ length: 150 }).map(() => ({
//         left: `${Math.random() * 100}%`,
//         top: `${Math.random() * 100}%`,
//         size: Math.random() * 2 + 1,
//         delay: `${Math.random() * 5}s`,
//         duration: `${2 + Math.random() * 3}s`,
//     }));

//     // Generate meteors
//     const meteors = Array.from({ length: 6 }).map(() => ({
//         left: `${Math.random() * 100}%`,
//         top: `${Math.random() * 30}%`,
//         width: 2,
//         height: 60 + Math.random() * 20,
//         delay: `${Math.random() * 10}s`,
//         duration: `${1.5 + Math.random() * 1.5}s`,
//         distanceX: 300 + Math.random() * 200,
//         distanceY: 300 + Math.random() * 200,
//     }));

//     return (
//         <>
//             <GlobalStyles
//                 styles={{
//                     '@keyframes orbit-rotate': {
//                         '0%': { transform: 'rotate(0deg)' },
//                         '100%': { transform: 'rotate(360deg)' },
//                     },
//                     '@keyframes planet-spin': {
//                         '0%': { transform: 'rotate(0deg)' },
//                         '100%': { transform: 'rotate(360deg)' },
//                     },
//                     '@keyframes sun-pulse': {
//                         '0%, 100%': {
//                             boxShadow: '0 0 320px rgba(255, 184, 107, 0.5), 0 0 960px rgba(255, 184, 107, 0.3)'
//                         },
//                         '50%': {
//                             boxShadow: '0 0 480px rgba(255, 184, 107, 0.8), 0 0 1440px rgba(255, 184, 107, 0.5)'
//                         },
//                     },
//                     '@keyframes star-blink': {
//                         '0%, 100%': { opacity: 1 },
//                         '50%': { opacity: 0.2 }
//                     },
//                     '@keyframes meteor-move': {
//                         '0%': { transform: 'translate(0,0) rotate(45deg)', opacity: 1 },
//                         '100%': { transform: 'translate(var(--dx), var(--dy)) rotate(45deg)', opacity: 0 }
//                     },
//                     '.starfield': {
//                         position: 'absolute',
//                         width: '100%',
//                         height: '100%',
//                         top: 0,
//                         left: 0,
//                         overflow: 'hidden',
//                         zIndex: 0,
//                     },
//                     '.star': {
//                         position: 'absolute',
//                         background: 'white',
//                         borderRadius: '50%',
//                         animation: 'star-blink 2s infinite ease-in-out',
//                     },
//                     '.meteor': {
//                         position: 'absolute',
//                         background: 'linear-gradient(to bottom, rgba(255,255,255,1), rgba(255,255,255,0))',
//                         width: '2px',
//                         borderRadius: '50%',
//                         transform: 'rotate(45deg)',
//                     }
//                 }}
//             />

//             <motion.div
//                 initial="hidden"
//                 animate="visible"
//                 variants={containerVariant}
//                 style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}
//             >
//                 {/* Starfield */}
//                 <div className="starfield">
//                     {stars.map((s, i) => (
//                         <div
//                             key={i}
//                             className="star"
//                             style={{
//                                 left: s.left,
//                                 top: s.top,
//                                 width: s.size,
//                                 height: s.size,
//                                 animationDelay: s.delay,
//                                 animationDuration: s.duration,
//                             }}
//                         />
//                     ))}

//                     {/* Meteors */}
//                     {meteors.map((m, i) => (
//                         <div
//                             key={i}
//                             className="meteor"
//                             style={{
//                                 left: m.left,
//                                 top: m.top,
//                                 height: m.height,
//                                 width: m.width,
//                                 animation: `meteor-move ${m.duration}s linear infinite`,
//                                 animationDelay: m.delay,
//                                 '--dx': `${m.distanceX}px`,
//                                 '--dy': `${m.distanceY}px`,
//                             } as any} // <-- cast to any to avoid TS error
//                         />
//                     ))}

//                 </div>

//                 <Box
//                     sx={{
//                         width: size,
//                         height: size,
//                         position: 'relative',
//                         display: 'grid',
//                         placeItems: 'center',
//                         userSelect: 'none',
//                         zIndex: 1,
//                     }}
//                 >
//                     {planets.map((p, i) => {
//                         const orbitRadius = p.distance;
//                         const orbitStyle = {
//                             position: 'absolute',
//                             width: `${orbitRadius * 2}px`,
//                             height: `${orbitRadius * 2}px`,
//                             left: `calc(50% - ${orbitRadius}px)`,
//                             top: `calc(50% - ${orbitRadius}px)`,
//                             borderRadius: '50%',
//                             border: '1px solid rgba(255,255,255,0.2)',
//                             boxShadow: '0 0 10px rgba(255,255,255,0.1)',
//                             animation: `orbit-rotate ${p.duration}s linear infinite`,
//                             animationDelay: `${i * -1.8}s`,
//                             transformOrigin: '50% 50%',
//                         };

//                         const planetStyle = {
//                             position: 'absolute',
//                             left: '50%',
//                             top: `${-(p.size) / 2}px`,
//                             transform: 'translateX(-50%)',
//                             width: `${p.size}px`,
//                             height: `${p.size}px`,
//                             borderRadius: '50%',
//                             background: p.color,
//                             boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
//                             display: 'flex',
//                             alignItems: 'center',
//                             justifyContent: 'center',
//                             fontSize: 9,
//                             color: '#000',
//                             fontWeight: 700,
//                             animation: `planet-spin 8s linear infinite`,
//                         };

//                         return (
//                             <Box key={i} sx={orbitStyle}>
//                                 <motion.div
//                                     initial={{ opacity: 0, scale: 0.85 }}
//                                     animate={{ opacity: 1, scale: 1 }}
//                                     transition={{ duration: 0.6, delay: 0.12 * i }}
//                                     style={{ width: '100%', height: '100%', position: 'relative' }}
//                                 >
//                                     <Box sx={planetStyle}>{p.label}</Box>
//                                 </motion.div>
//                             </Box>
//                         );
//                     })}

//                     {/* Sun */}
//                     <motion.div variants={sunVariant}>
//                         <Box
//                             sx={{
//                                 width: sunSize,
//                                 height: sunSize,
//                                 borderRadius: '999px',
//                                 display: 'grid',
//                                 placeItems: 'center',
//                                 background: 'radial-gradient(circle at 30% 30%, #FFE680, #FFB86B 55%, #FF9A4D 100%)',
//                                 boxShadow:
//                                     '0 12px 30px rgba(255,165,60,0.18), 0 6px 14px rgba(0,0,0,0.18), inset 0 -8px 20px rgba(0,0,0,0.06)',
//                                 border: '1px solid rgba(255,255,255,0.18)',
//                                 animation: 'sun-pulse 3s ease-in-out infinite',
//                                 overflow: 'hidden',
//                                 zIndex: 2,
//                             }}
//                         >
//                             <Box
//                                 component="img"
//                                 src={sunImage}
//                                 alt="Elo"
//                                 sx={{
//                                     width: '80%',
//                                     height: '80%',
//                                     borderRadius: '50%',
//                                     objectFit: 'cover',
//                                     boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
//                                 }}
//                             />
//                         </Box>
//                     </motion.div>
//                 </Box>
//             </motion.div>
//         </>
//     );
// }
